import { Button } from '@nextui-org/react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { joinBillingAccount, JoinByInvitationResponse } from '~/billing/services/billingService'
import Typography from '~/components/common/Typography'
import { useBillingAccounts } from '~/contexts/BillingAccountsContext'
import { useUserContext } from '~/contexts/UserContext'
import { BetterToast } from '~/utils'

function LoadingSpinner() {
  return (
    <div className='flex items-center justify-center h-screen'>
      <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500'></div>
    </div>
  )
}

function JoinAccountPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isAuthenticated, loaded } = useUserContext()
  const { refetch: refetchBillingAccounts, setSelectedBillingAccountId } = useBillingAccounts()
  const [joinData, setJoinData] = useState<JoinByInvitationResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  const invitationCode = searchParams.get('invitation')
  const msg = searchParams.get('msg')

  useEffect(() => {
    // Wait for auth state to load
    if (!loaded) return

    // If no invitation code, show error
    if (!invitationCode) {
      setError('Invite link is invalid or expired')
      return
    }

    // If not authenticated, redirect to signin with invitation preserved
    if (!isAuthenticated) {
      const signinMsg = 'Please sign in to accept the invitation'
      const params = new URLSearchParams(searchParams)
      params.set('msg', signinMsg)
      navigate(`/signin?${params.toString()}`, { replace: true })
      return
    }

    // If authenticated and we have invitation code, and haven't processed yet
    if (!joinData && !isProcessing && !error) {
      setIsProcessing(true)
      setError(null)

      // Call backend endpoint
      joinBillingAccount(invitationCode)
        .then(data => {
          BetterToast.success(data.message)
          setJoinData(data)
        })
        .catch(err => {
          console.error('Error joining account:', err)
          const errorMessage =
            err instanceof Error ? err.message : 'Failed to join account. Please try again.'
          setError(errorMessage)
          setIsProcessing(false)
        })
    }
  }, [
    loaded,
    isAuthenticated,
    invitationCode,
    joinData,
    isProcessing,
    error,
    searchParams,
    navigate
  ])

  useEffect(() => {
    if (!joinData || !isProcessing) return

    // Refresh billing accounts context and select the joined account
    refetchBillingAccounts().then(() => {
      setSelectedBillingAccountId(joinData.billing_id)
      setIsProcessing(false)
      // Navigate to home after successful join
      navigate('/', { replace: true })
    })
  }, [joinData, refetchBillingAccounts, setSelectedBillingAccountId, isProcessing, navigate])

  const retryJoin = () => {
    setError(null)
    setIsProcessing(false)
  }

  // Show loading while auth state is being determined
  if (!loaded) {
    return <LoadingSpinner />
  }

  // Show processing state
  if (isProcessing) {
    return (
      <div className='flex items-center justify-center h-screen'>
        <div className='text-center max-w-md p-8'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-6 mx-auto'></div>
          <Typography
            fontFamily='geist'
            fontSize='titleLg'
            fontWeight='semibold'
            className='text-gray-900 mb-2'
          >
            Joining Account
          </Typography>
          <Typography fontFamily='geist' fontSize='bodyMd' className='text-gray-600'>
            Please wait while we add you to the account...
          </Typography>
        </div>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className='flex items-center justify-center h-screen'>
        <div className='text-center max-w-md p-8'>
          <div className='text-red-500 mb-6'>
            <svg
              className='w-16 h-16 mx-auto'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.962-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z'
              />
            </svg>
          </div>
          <Typography
            fontFamily='geist'
            fontSize='titleLg'
            fontWeight='semibold'
            className='text-gray-900 mb-2'
          >
            Unable to Join Account
          </Typography>
          <Typography fontFamily='geist' fontSize='bodyMd' className='text-gray-600 mb-6'>
            {error}
          </Typography>
          <div className='flex gap-3 justify-center'>
            <Button variant='bordered' onClick={retryJoin} disabled={isProcessing}>
              Try Again
            </Button>
            <Button color='primary' onClick={() => navigate('/')}>
              Go Home
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Show message if present (for cases like already in account)
  if (msg) {
    return (
      <div className='flex items-center justify-center h-screen'>
        <div className='text-center max-w-md p-8'>
          <Typography
            fontFamily='geist'
            fontSize='titleLg'
            fontWeight='semibold'
            className='text-gray-900 mb-2'
          >
            Account Invitation
          </Typography>
          <Typography fontFamily='geist' fontSize='bodyMd' className='text-gray-600 mb-6'>
            {msg}
          </Typography>
          <Button color='primary' onClick={() => navigate('/')}>
            Continue
          </Button>
        </div>
      </div>
    )
  }

  // Fallback - should not normally reach here
  return <LoadingSpinner />
}

export default JoinAccountPage
